<template>
  <div v-if="issueData"
       class="target-view"
       ref="targetView">
    <div class="capture-container"
         ref="captureContainer">
      <div class="image-container"
        :style="{
          width: imageSize.width + 'px',
          height: imageSize.height + 'px',
          margin: reportDatum.deviceType === 3 ? '0 auto' : '0',
          backgroundImage: documentView ? `url('${imageUrl}')` : null
        }">
        <img :src="imageUrl"
             alt=""
             :width="imageSize.width" :height="imageSize.height"
             @error="$emit('imageLoaded')"
             @load="$emit('imageLoaded')" />

        <div v-if="highlightStyle" class="captureHighlight" ref='captureHighlight'
          :style="highlightStyle"></div>

        <div v-if="maskStyle" class="capture-mask">
          <div :style="maskStyle.top"></div>
          <div :style="maskStyle.left"></div>
          <div :style="maskStyle.right"></div>
          <div :style="maskStyle.bottom"></div>
        </div>

        <div v-if="guideStyle" class="capture-guide">
          <div :style="guideStyle.top"></div>
          <div :style="guideStyle.left"></div>
          <div :style="guideStyle.right"></div>
          <div :style="guideStyle.bottom"></div>
        </div>
      </div>
    </div>
    <div v-if="isShowNoRectLayer"
         class="issue-no-rect">
      <div class="issue-no-rect-wrap">
        <i class="icon icon--alert"></i>
        <p v-html="$t('report.noCaptureRect')"></p>
        <button type="button"
                class="btn_3"
                @click="hideNoRectLayer">Close</button>
      </div>
    </div>
  </div>
</template>

<script>
import { apiImageUrl } from '@/lib/http-client'

export default {
  name: 'ReportCapture',
  props: ['report', 'issueData', 'nodeDataMap', 'currentIssue', 'documentView'],
  data () {
    return {
      scale: 0,
      isShowNoRectLayer: false,
      highlightStyle: null,
      maskStyle: null,
      guideStyle: null
    }
  },
  computed: {
    reportDatum () {
      return this.report.ReportDatum
    },
    imageUrl () {
      return apiImageUrl(this.report.imageUrl)
    },
    imageSize () {
      return {
        width: Math.floor(this.scale * this.issueData.imageSize.width),
        height: Math.floor(this.scale * this.issueData.imageSize.height)
      }
    }
  },
  watch: {
    scale () {
      this.$emit('onImageSizeChanged')
    },
    currentIssue (value) {
      this.isShowNoRectLayer = false

      if (value) {
        const rect = this.currentIssue.rect

        if (rect && rect.width > 0 && rect.height > 0) {
          const frameId = this.currentIssue.parentFrameId

          if (frameId) {
            const frameData = this.nodeDataMap[frameId]

            if (frameData.accessible && frameData.rect) {
              rect.top += frameData.rect.top
              rect.left += frameData.rect.left
            }
          }
          const imageSize = this.imageSize
          let top = Math.floor(rect.top * this.scale)
          let left = Math.floor(rect.left * this.scale)
          let width = Math.ceil(rect.width * this.scale)
          let height = Math.ceil(rect.height * this.scale)
          const isInImage = top + height > 0 && left < imageSize.width &&
            left + width > 0 && top < imageSize.height

          if (isInImage) {
            if (left < 0) {
              left = 0
            }

            if (top < 0) {
              top = 0
            }

            if (top + height > imageSize.height) {
              height = imageSize.height - top
            }

            if (left + width > imageSize.width) {
              width = imageSize.width - left
            }

            this.highlightStyle = {
              width: width + 'px',
              height: height + 'px',
              left: left + 'px',
              top: top + 'px'
            }

            this.maskStyle = {
              top: {
                bottom: 'auto',
                height: top + 'px'
              },
              left: {
                top: top + 'px',
                width: left + 'px',
                height: height + 'px',
                bottom: 'auto',
                right: 'auto'
              },
              bottom: {
                top: (top + height) + 'px'
              },
              right: {
                top: top + 'px',
                left: (left + width) + 'px',
                height: height + 'px',
                bottom: 'auto'
              }
            }

            const halfWidth = Math.round(width / 2)
            const halfHeight = Math.round(height / 2)

            this.guideStyle = {
              top: {
                width: '1px',
                left: (left + halfWidth) + 'px',
                height: top + 'px',
                right: 'auto',
                bottom: 'auto',
                marginLeft: '-0.5px'
              },
              left: {
                height: '1px',
                top: (top + halfHeight) + 'px',
                width: left + 'px',
                bottom: 'auto',
                marginTop: '-0.5px'
              },
              bottom: {
                width: '1px',
                left: (left + halfWidth) + 'px',
                top: (top + height) + 'px',
                right: 'auto',
                marginLeft: '-0.5px'
              },
              right: {
                height: '1px',
                top: (top + halfHeight) + 'px',
                left: (left + width) + 'px',
                bottom: 'auto',
                marginTop: '-0.5px'
              }
            }

            const containerCenter = {
              top: Math.round(this.$el.offsetHeight / 2),
              left: Math.round(this.$el.offsetWidth / 2)
            }

            const elCenter = {
              top: top + Math.round(height / 2),
              left: left + Math.round(width / 2)
            }

            let scrollLeft = elCenter.left - containerCenter.left
            let scrollTop = elCenter.top - containerCenter.top

            if (scrollLeft < 0) {
              scrollLeft = 0
            }

            if (scrollTop < 0) {
              scrollTop = 0
            }

            this.$refs.captureContainer.scrollLeft = scrollLeft
            this.$refs.captureContainer.scrollTop = scrollTop
            return
          }
        }
      }

      if (value) {
        this.isShowNoRectLayer = true
      }

      this.$refs.captureContainer.scrollLeft = 0
      this.$refs.captureContainer.scrollTop = 0
      this.highlightStyle = null
      this.maskStyle = null
      this.guideStyle = null
    }
  },
  mounted () {
    this.resizeTimer = window.setInterval(() => {
      const containerEl = this.$refs.targetView
      const containerWidth = containerEl.scrollWidth
      const imageWidth = this.issueData.imageSize.width

      if (imageWidth === 0) {
        return
      }

      if (imageWidth >= containerWidth) {
        this.scale = containerWidth / imageWidth
      } else {
        this.scale = 1
      }
    }, 500)
  },
  methods: {
    hideNoRectLayer () {
      this.isShowNoRectLayer = false
    }
  },
  destroyed () {
    window.clearInterval(this.resizeTimer)
  }
}
</script>

<style lang="scss" scoped>
.target-view {
  width:100%;
  position: relative;
  flex-basis: 320px;
  flex-grow: 1;
  overflow: hidden;

  .capture-container {
    width: 100%;
    height: 100%;
    overflow-x: hidden;
    overflow-y: auto;
    text-align: left;
    position: relative;
    border-radius: 10px 10px 0 0;
  }

  .image-container {
    position: relative;
    background-size: 100% 100%;
    -webkit-print-color-adjust: exact !important;
    color-adjust: exact !important;

    img {
      border-radius: 10px 10px 0 0;
    }
  }

  .capture-mask > div,
  .capture-guide > div {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.6)
  }

  .capture-guide > div {
    background-color: #ffe30d;
  }

  .issue-no-rect {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.6);

    .issue-no-rect-wrap {
      width: 280px;
      position: absolute;
      left: 50%;
      top: 50%;
      margin-top: -70px;
      margin-left: -150px;
      box-sizing: border-box;
      padding: 15px 30px;
      border-radius: 8px;
      box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.08);
      background-color: #ffffff;
      text-align: center;
      color: #444444;

      i {
        margin: 8px 0 6px;
      }

      p {
        font-size: 16px;
        font-weight: 500;
        line-height: 1.31;
        color: #444444;
        margin-bottom: 15px;

        & /deep/ span {
          color: #ec0047;
        }
      }

      button {
        font-size: 13px;
        height: 36px;
        line-height: 34px;
      }
    }
  }

  .captureHighlight {
    position: absolute;
    outline: 1px solid #ffe30d;
    z-index: 1;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/valid-member/report/ReportCapture.vue